import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import RightSideMenu from "@/components/navigation/RightSideMenu";
import AdPanel from "@/components/ads/AdPanel";
import { MapPin, Zap, Calendar, Clock, Plus } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { profileRouterService } from "@/services/profileRouterService";
import { AlphaLoader } from "@/components/ui/alpha-loader";
import { eventService, EventWithAttendees, EventInsert } from "@/services/eventService";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import EventCard from "@/components/events/EventCard";
import CreateEventDialog from "@/components/events/CreateEventDialog";
import EventDetailsDialog from "@/components/events/EventDetailsDialog";

interface ProfileData {
  id: string;
  user_id: string;
  display_name: string;
  photo_urls: string[];
  location: string;
  distance?: number;
  interaction_date?: string;
  view_count?: number; // Added view count for visitors
}

const Buzzz = () => {
  const { user } = useAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [visitors, setVisitors] = useState<ProfileData[]>([]);
  const [buzzzReceived, setBuzzzReceived] = useState<ProfileData[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<EventWithAttendees | null>(null);

  // Query for events with proper filtering
  const { data: allEvents = [] } = useQuery({
    queryKey: ["events"],
    queryFn: eventService.getEvents,
  });

  // Filter events for current user (created or attending)
  const privateEvents = allEvents.filter(event => 
    event.created_by === user?.id || 
    event.attendees.some(attendee => attendee.user_id === user?.id)
  );

  // Event mutations
  const createEventMutation = useMutation({
    mutationFn: async (eventData: EventInsert) => {
      if (!user?.id) throw new Error("User not authenticated");
      return eventService.createEvent({
        ...eventData,
        created_by: user.id
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      setIsCreateDialogOpen(false);
      toast({
        title: "Event Created!",
        description: "Your event has been created successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to create event. Please try again.",
        variant: "destructive",
      });
      console.error("Create event error:", error);
    },
  });

  const requestAttendanceMutation = useMutation({
    mutationFn: eventService.requestAttendance,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast({
        title: "Request Sent!",
        description: "Your attendance request has been sent to the event organizer",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to send attendance request",
        variant: "destructive",
      });
      console.error("Request attendance error:", error);
    },
  });

  const updateAttendeeMutation = useMutation({
    mutationFn: ({ attendeeId, status }: { attendeeId: string; status: "approved" | "declined" }) =>
      eventService.updateAttendeeStatus(attendeeId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast({
        title: "Attendee Updated",
        description: "Attendee status has been updated",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update attendee status",
        variant: "destructive",
      });
      console.error("Update attendee error:", error);
    },
  });

  const promoteToAdminMutation = useMutation({
    mutationFn: eventService.promoteToAdmin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast({
        title: "Promoted to Admin",
        description: "User has been promoted to event admin",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to promote user to admin",
        variant: "destructive",
      });
      console.error("Promote to admin error:", error);
    },
  });

  const removeAttendeeMutation = useMutation({
    mutationFn: eventService.removeAttendee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      toast({
        title: "Attendee Removed",
        description: "Attendee has been removed from the event",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to remove attendee",
        variant: "destructive",
      });
      console.error("Remove attendee error:", error);
    },
  });

  // Event handlers
  const handleCreateEvent = async (eventData: EventInsert) => {
    await createEventMutation.mutateAsync(eventData);
  };

  const handleRequestAttendance = (eventId: string) => {
    requestAttendanceMutation.mutate(eventId);
  };

  const handleApproveAttendee = (attendeeId: string) => {
    updateAttendeeMutation.mutate({ attendeeId, status: "approved" });
  };

  const handleDeclineAttendee = (attendeeId: string) => {
    updateAttendeeMutation.mutate({ attendeeId, status: "declined" });
  };

  const handlePromoteToAdmin = (attendeeId: string) => {
    promoteToAdminMutation.mutate(attendeeId);
  };

  const handleRemoveAttendee = (attendeeId: string) => {
    removeAttendeeMutation.mutate(attendeeId);
  };

  useEffect(() => {
    if (user) {
      loadActivityData();
    }
  }, [user]);

  const loadActivityData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Load visitors with view count (people who viewed my profile)
      const { data: visitorsData } = await supabase
        .from('profile_views')
        .select('viewer_id, viewed_at')
        .eq('viewed_id', user.id)
        .order('viewed_at', { ascending: false });

      // Group by viewer_id and count views
      const viewerCounts: { [key: string]: { count: number; lastViewed: string } } = {};
      visitorsData?.forEach(view => {
        if (!viewerCounts[view.viewer_id]) {
          viewerCounts[view.viewer_id] = { count: 0, lastViewed: view.viewed_at };
        }
        viewerCounts[view.viewer_id].count++;
        // Keep the most recent view date
        if (view.viewed_at > viewerCounts[view.viewer_id].lastViewed) {
          viewerCounts[view.viewer_id].lastViewed = view.viewed_at;
        }
      });

      const uniqueViewerIds = Object.keys(viewerCounts);
      const { data: visitorProfiles } = await supabase
        .from('user_profiles')
        .select('user_id, display_name, photo_urls, location')
        .in('user_id', uniqueViewerIds);

      const processedVisitors = uniqueViewerIds.map(viewerId => {
        const profile = visitorProfiles?.find(p => p.user_id === viewerId);
        const viewData = viewerCounts[viewerId];
        return {
          id: viewerId,
          user_id: viewerId,
          display_name: profile?.display_name || 'Anonymous',
          photo_urls: profile?.photo_urls || [],
          location: profile?.location || '',
          interaction_date: viewData.lastViewed,
          view_count: viewData.count,
          distance: Math.floor(Math.random() * 10) + 1
        };
      });

      setVisitors(processedVisitors);

      // Private events are now handled by React Query

      // Load buzzz received - keep existing logic
      const { data: buzzzData } = await supabase
        .from('interactions')
        .select('user_id, created_at')
        .eq('target_user_id', user.id)
        .eq('interaction_type', 'buzzz')
        .order('created_at', { ascending: false })
        .limit(50);

      const buzzzUserIds = buzzzData?.map(b => b.user_id) || [];
      const { data: buzzzProfiles } = await supabase
        .from('user_profiles')
        .select('user_id, display_name, photo_urls, location')
        .in('user_id', buzzzUserIds);

      const processedBuzzz = buzzzData?.map(buzzz => {
        const profile = buzzzProfiles?.find(p => p.user_id === buzzz.user_id);
        return {
          id: buzzz.user_id,
          user_id: buzzz.user_id,
          display_name: profile?.display_name || 'Anonymous',
          photo_urls: profile?.photo_urls || [],
          location: profile?.location || '',
          interaction_date: buzzz.created_at
        };
      }) || [];

      setBuzzzReceived(processedBuzzz);
    } catch (error) {
      console.error('Error loading activity data:', error);
      toast({
        title: "Error",
        description: "Failed to load activity data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileClick = (userId: string) => {
    console.log('Profile clicked:', userId);
    navigate(profileRouterService.getProfileRoute(userId));
  };

  const ProfileCard = ({ profile, showDistance = false, showViewCount = false }: { 
    profile: ProfileData; 
    showDistance?: boolean;
    showViewCount?: boolean;
  }) => {
    const timeAgo = profile.interaction_date ? new Date(profile.interaction_date).toLocaleDateString() : '';
    const isOnline = profile.interaction_date && new Date(profile.interaction_date) > new Date(Date.now() - 15 * 60 * 1000);

    return (
      <div 
        className="relative aspect-[3/4] rounded-lg overflow-hidden cursor-pointer group bg-gray-900/50"
        onClick={() => handleProfileClick(profile.user_id)}
      >
        {profile.photo_urls?.[0] ? (
          <img 
            src={profile.photo_urls[0]} 
            alt={profile.display_name} 
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" 
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
            <div className="text-4xl font-bold text-gray-600">
              {profile.display_name.charAt(0)}
            </div>
          </div>
        )}
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
        
        {/* Top right indicators */}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {/* Online indicator */}
          {isOnline && (
            <div className="w-3 h-3 rounded-full bg-toxic-green shadow-lg border-2 border-white" />
          )}
          
          {/* View count indicator */}
          {showViewCount && profile.view_count && profile.view_count > 1 && (
            <div className="bg-black/70 text-white text-xs px-2 py-1 rounded-full font-medium border border-white/20">
              {profile.view_count}x
            </div>
          )}
        </div>
        
        {/* Profile info overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-3 text-white">
          <h3 className="font-semibold text-lg leading-tight mb-1">
            {profile.display_name}
          </h3>
          
          {profile.location && (
            <div className="flex items-center text-sm text-gray-300 mb-1">
              <MapPin className="w-3 h-3 mr-1" />
              <span className="truncate">{profile.location}</span>
            </div>
          )}
          
          <div className="flex items-center justify-between text-xs text-gray-400">
            <span>{timeAgo}</span>
            {showDistance && profile.distance && (
              <span className="bg-black/50 px-2 py-1 rounded">
                {profile.distance}km
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-carbon text-ice-white">
        <RightSideMenu />
        <AdPanel />
        <div className="flex items-center justify-center" style={{ height: 'calc(100vh - calc(100vh / 7))' }}>
          <AlphaLoader size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-carbon text-ice-white ${isRTL ? 'rtl' : 'ltr'}`}>
      <RightSideMenu />
      <AdPanel />
      <div className="container mx-auto px-4 py-2 max-w-6xl">
        <Tabs defaultValue="buzzz" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4 bg-carbon/50 border border-ice-white/20 mx-0 py-0 px-[14px] my-0">
            <TabsTrigger value="buzzz" className="data-[state=active]:bg-neon-red data-[state=active]:text-ice-white text-ice-white/70 font-medium my-[6px] py-0">
              {t('buzzz_received')} ({buzzzReceived.length})
            </TabsTrigger>
            <TabsTrigger value="visitors" className="data-[state=active]:bg-neon-red data-[state=active]:text-ice-white text-ice-white/70 font-medium py-0">
              {t('visitors')} ({visitors.length})
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-neon-red data-[state=active]:text-ice-white text-ice-white/70 font-medium py-0 px-[9px]">
              Private Events ({privateEvents.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="buzzz" className="mt-0">
            {buzzzReceived.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-ice-white/40 text-lg mb-2">{t('no_buzzz')}</div>
                <div className="text-ice-white/60 text-sm">Buzzz you receive will appear here</div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {buzzzReceived.map(profile => (
                  <ProfileCard key={profile.id} profile={profile} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="visitors" className="mt-0">
            {visitors.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-ice-white/40 text-lg mb-2">{t('no_visitors')}</div>
                <div className="text-ice-white/60 text-sm">Your profile visitors will appear here</div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {visitors.map(profile => (
                  <ProfileCard 
                    key={profile.id} 
                    profile={profile} 
                    showDistance={true} 
                    showViewCount={true}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="events" className="mt-0">
            {privateEvents.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-ice-white/40 text-lg mb-2">No Private Events</div>
                <div className="text-ice-white/60 text-sm mb-4">Events you create or attend will appear here</div>
                <Button 
                  onClick={() => setIsCreateDialogOpen(true)}
                  className="bg-neon-red hover:bg-neon-red/80 text-ice-white"
                >
                  Create First Event
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {privateEvents.map(event => (
                  <EventCard 
                    key={event.id} 
                    event={event}
                    onRequestAttendance={handleRequestAttendance}
                    onViewDetails={setSelectedEvent}
                    isRequesting={requestAttendanceMutation.isPending}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Floating Create Button */}
      <Button
        onClick={() => setIsCreateDialogOpen(true)}
        className="fixed bottom-20 right-4 z-50 w-14 h-14 rounded-full bg-neon-red hover:bg-neon-red/80 shadow-lg"
        size="icon"
      >
        <Plus className="w-6 h-6" />
      </Button>

      {/* Create Event Dialog */}
      <CreateEventDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onCreateEvent={handleCreateEvent}
        isCreating={createEventMutation.isPending}
      />

      {/* Event Details Dialog */}
      <EventDetailsDialog
        event={selectedEvent}
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onApproveAttendee={handleApproveAttendee}
        onDeclineAttendee={handleDeclineAttendee}
        onPromoteToAdmin={handlePromoteToAdmin}
        onRemoveAttendee={handleRemoveAttendee}
        isUpdating={updateAttendeeMutation.isPending || promoteToAdminMutation.isPending || removeAttendeeMutation.isPending}
      />
    </div>
  );
};

export default Buzzz;
